import type { PortIndicator } from '../../Port/utils';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { chassisVars } from '../../Chassis/atoms/utils';
import { SlotIndicatorIcon } from './SlotIndicators';
import { slotVars } from './utils';

const SlotCaptionContainer = styled('div', {
  zIndex: slotVars.zIndex.caption,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  widthAll: `$${chassisVars.slot.width}`,
  heightAll: `$${chassisVars.slot.caption}`,
  padding: `0 $${chassisVars.slot.indicatorInset}`,
});
SlotCaptionContainer.displayName = 'SlotCaptionContainer';

/**
 * The number chip, right-aligned so its last digit lands on the same column in every slot whether or
 * not anything sits beside it.
 *
 * `flex: 1` is what pins that edge: the chip takes whatever the icons leave, so its right edge is the
 * rail's inset either way and only its left one moves. A label too wide for the room left over
 * overflows leftward, under those icons, rather than growing the slot and shunting its neighbours off
 * their column.
 */
const SlotCaptionNumber = styled('span', {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: 0,
  color: colors.bodyNeutralLight,
  fontFamily: '$mono',
  fontSize: '$9',
  lineHeight: `$${chassisVars.slot.caption}`,
  whiteSpace: 'nowrap',

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    selected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotCaptionNumber.displayName = 'SlotCaptionNumber';

export type SlotCaptionProps = {
  /**
   * The slot's indicators, from `getPortIndicators`. The ones placed in the caption — PoE today —
   * are drawn to the left of the number, on the row it already occupies, so the rail keeps both of
   * its ends for everything else; the rest belong to `SlotIndicators` and are ignored here.
   */
  indicators?: PortIndicator[];
  /** What the chip reads: a port number, or a name for faceplates that use one ('WAN0', 'OOB'). */
  number?: string | number | null;
  selected?: boolean;
};

/**
 * The row along a slot's inner edge: the number chip, and the properties that read better beside it
 * than out on the rail.
 *
 * PoE lives here rather than on the rail because it belongs to the port as a fact about the socket,
 * the way its number does — and putting it here hands the rail's left end back to the properties
 * that describe what the port is *doing*, which is what changes while you're looking at it.
 */
export function SlotCaption({ indicators = [], number, selected }: SlotCaptionProps) {
  return (
    <SlotCaptionContainer>
      {indicators
        .filter((indicator) => indicator.placement === 'caption')
        .map((indicator) => (
          <SlotIndicatorIcon key={indicator.id} indicator={indicator} />
        ))}
      <SlotCaptionNumber selected={selected}>{number}</SlotCaptionNumber>
    </SlotCaptionContainer>
  );
}
